import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Heart, Crown, Check, Loader2, Send, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { usePlatform } from '../hooks/usePlatform';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  { id: '1month', label: '1 Maand', price: 2.99, days: 30, popular: false },
  { id: '3months', label: '3 Maanden', price: 7.99, days: 90, popular: true, save: '11%' },
  { id: '1year', label: '1 Jaar', price: 29.99, days: 365, popular: false, save: '16%' },
];

const GiftSubscription = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const { t } = useLanguage();
  const { hideWebPurchaseUI } = usePlatform();
  const [selectedPlan, setSelectedPlan] = useState('3months');
  const [form, setForm] = useState({ recipient_name: '', recipient_email: '', sender_name: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handlePurchase = async () => {
    if (!isAuthenticated) {
      toast.error('Log eerst in om een cadeau te kopen');
      navigate('/login');
      return;
    }
    if (!form.sender_name.trim() || !form.recipient_name.trim()) {
      toast.error('Vul je naam en de naam van de ontvanger in');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/gift/purchase`, {
        plan: selectedPlan,
        ...form
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Er ging iets mis');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pt-20 md:pt-24 pb-32 px-4" data-testid="gift-page">
      <SEO path="/gift" />
      <div className="max-w-3xl mx-auto">
        {hideWebPurchaseUI ? (
          <div className="mt-10">
            <div className="text-center mb-8">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-old-rose/30 to-gold/30 flex items-center justify-center mx-auto mb-4">
                <Gift className="w-10 h-10 text-old-rose" />
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-foreground mb-3">
                Geef Verhalen Cadeau
              </h1>
              <p className="text-muted-foreground text-base md:text-lg max-w-lg mx-auto">
                Cadeau-abonnementen worden beheerd op onze website.
              </p>
            </div>
            <Card className="max-w-md mx-auto" data-testid="gift-ios-notice">
              <CardContent className="p-6 text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  Koop een cadeauabonnement op omasverhaaltjes.nl en ontvang direct een unieke code om te delen.
                </p>
                <a
                  href="https://omasverhaaltjes.nl/gift"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-gold font-semibold hover:underline"
                  data-testid="gift-ios-link"
                >
                  Ga naar omasverhaaltjes.nl/gift
                  <ExternalLink className="w-4 h-4" />
                </a>
              </CardContent>
            </Card>
          </div>
        ) : (
        <>
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-old-rose/30 to-gold/30 flex items-center justify-center mx-auto mb-4">
            <Gift className="w-10 h-10 text-old-rose" />
          </div>
          <h1 className="font-display text-4xl md:text-5xl text-foreground mb-3">
            Geef Verhalen Cadeau
          </h1>
          <p className="text-muted-foreground text-base md:text-lg max-w-lg mx-auto">
            Het mooiste cadeau voor een oma, opa of gezin — een wereld vol magische verhaaltjes
          </p>
        </div>

        {/* Plan Selection */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {PLANS.map((plan) => (
            <Card
              key={plan.id}
              data-testid={`gift-plan-${plan.id}`}
              className={`cursor-pointer transition-all hover:shadow-lg relative ${
                selectedPlan === plan.id
                  ? 'ring-2 ring-gold shadow-lg scale-[1.02]'
                  : 'hover:scale-[1.01]'
              }`}
              onClick={() => setSelectedPlan(plan.id)}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-night-blue text-xs font-bold px-3 py-1 rounded-full">
                  POPULAIR
                </div>
              )}
              <CardContent className="p-6 text-center">
                <h3 className="font-display text-xl mb-1">{plan.label}</h3>
                <div className="flex items-baseline justify-center gap-1 mb-2">
                  <span className="text-3xl font-bold text-foreground">€{plan.price.toFixed(2).replace('.', ',')}</span>
                </div>
                {plan.save && (
                  <span className="inline-block text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded-full font-medium mb-2">
                    Bespaar {plan.save}
                  </span>
                )}
                <p className="text-sm text-muted-foreground">{plan.days} dagen premium</p>
                <div className={`mt-3 w-5 h-5 rounded-full border-2 mx-auto flex items-center justify-center ${
                  selectedPlan === plan.id ? 'border-gold bg-gold' : 'border-muted'
                }`}>
                  {selectedPlan === plan.id && <Check className="w-3 h-3 text-night-blue" />}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Gift Form */}
        <Card className="mb-8">
          <CardContent className="p-6 md:p-8 space-y-5">
            <h2 className="font-display text-2xl flex items-center gap-2">
              <Heart className="w-5 h-5 text-old-rose" />
              Personaliseer je cadeau
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Jouw naam *</Label>
                <Input
                  data-testid="gift-sender-name"
                  placeholder="Bijv. Oma Wilma"
                  value={form.sender_name}
                  onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Naam ontvanger *</Label>
                <Input
                  data-testid="gift-recipient-name"
                  placeholder="Bijv. Familie De Vries"
                  value={form.recipient_name}
                  onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>E-mail ontvanger (optioneel)</Label>
              <Input
                data-testid="gift-recipient-email"
                type="email"
                placeholder="De ontvanger krijgt een e-mail met de cadeaucode"
                value={form.recipient_email}
                onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Persoonlijk bericht (optioneel)</Label>
              <Textarea
                data-testid="gift-message"
                placeholder="Lieve schat, hier zijn magische verhaaltjes speciaal voor jou..."
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Purchase Button */}
        <Button
          data-testid="gift-purchase-btn"
          onClick={handlePurchase}
          disabled={loading}
          className="w-full rounded-full bg-old-rose hover:bg-old-rose/90 text-night-blue font-bold py-6 text-lg shadow-lg"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <Gift className="w-5 h-5 mr-2" />
          )}
          Cadeau kopen — €{PLANS.find(p => p.id === selectedPlan)?.price.toFixed(2).replace('.', ',')}
        </Button>

        <p className="text-center text-xs text-muted-foreground mt-3">
          Veilig betalen via Mollie. Na betaling ontvang je een unieke cadeaucode.
        </p>
        </>
        )}

        {/* Redeem Section — visible on ALL platforms (not a purchase) */}
        <div className="mt-12 pt-8 border-t border-border">
          <RedeemGift />
        </div>
      </div>
    </div>
  );
};

const RedeemGift = () => {
  const { isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  // Check URL for gift code
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlCode = params.get('code');
    if (urlCode) setCode(urlCode);
  }, []);

  const handleRedeem = async () => {
    if (!isAuthenticated) {
      toast.error('Log eerst in om je cadeau te activeren');
      navigate('/login');
      return;
    }
    if (!code.trim()) {
      toast.error('Vul je cadeaucode in');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/gift/redeem`, { code: code.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      navigate('/');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ongeldige code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="text-center" data-testid="gift-redeem-section">
      <h2 className="font-display text-2xl mb-2 flex items-center justify-center gap-2">
        <Crown className="w-5 h-5 text-gold" />
        Cadeaucode inwisselen
      </h2>
      <p className="text-muted-foreground text-sm mb-4">Heb je een cadeaucode ontvangen? Activeer hem hier!</p>
      <div className="flex gap-2 max-w-md mx-auto">
        <Input
          data-testid="gift-redeem-input"
          placeholder="CADEAU-XXXXXXXX"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          className="text-center font-mono text-lg tracking-wider"
        />
        <Button
          data-testid="gift-redeem-btn"
          onClick={handleRedeem}
          disabled={loading || !code.trim()}
          className="rounded-full bg-gold hover:bg-gold/90 text-night-blue font-bold px-6"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Activeer'}
        </Button>
      </div>
    </div>
  );
};

export default GiftSubscription;
